import { Suspense, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar";
import { BlobBackground } from "@/pages/onboarding/BlobBackground";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageTransition } from "@/components/layout/PageTransition";
import { ContentSkeleton } from "@/components/layout/ContentSkeleton";
import { PageChromeProvider } from "@/components/layout/PageChrome";
import { useNavTree } from "@/components/sidebar/hooks/useNavTree";
import { isScrollableRoute } from "@/constants/scrollableRoutes";

/**
 * Persistent shell for every protected page. The sidebar and header mount here
 * once and survive navigation; only the Outlet swaps. The Suspense boundary is
 * inside the shell so a lazy page chunk shows a content skeleton rather than
 * blanking the sidebar and header.
 *
 * The nav tree is built once here and handed to both the sidebar and the
 * breadcrumb header, so the two can never disagree and the tree is not
 * recomputed per surface.
 */
export function AppShell() {
  const { pathname } = useLocation();
  const sections = useNavTree();
  const overflowClass = isScrollableRoute(pathname) ? "overflow-y-auto" : "overflow-hidden";
  // The home lobby's ambient wash spans the WHOLE viewport — sidebar and
  // header included — so it lives here, behind the shell (first in paint
  // order). The sidebar and header go transparent on this route so the wash
  // reads through them; every other route keeps its opaque chrome.
  const isHomeLobby = pathname === "/ui";

  const mainRef = useRef<HTMLElement>(null);
  const isFirstRender = useRef(true);

  // On navigation, hand focus to the content region and reset its scroll, so a
  // keyboard user lands in the new page rather than on the link they clicked and
  // the page doesn't open half-scrolled. Skipped on first paint, and never steals
  // focus a page has already placed inside itself (the chat composer autofocuses).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const main = mainRef.current;
    if (!main) return;
    main.scrollTop = 0;
    if (!main.contains(document.activeElement)) {
      main.focus({ preventScroll: true });
    }
  }, [pathname]);

  return (
    <PageChromeProvider>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-v2-md focus:bg-v2-background focus:px-3 focus:py-2 focus:text-v2-sm focus:text-v2-foreground focus:shadow-md focus:outline-none focus:ring-2 focus:ring-v2-ring"
      >
        Skip to content
      </a>
      {isHomeLobby && <BlobBackground />}
      <div className="flex h-screen">
        <AppSidebar sections={sections} />
        <div className="flex min-w-0 flex-1 flex-col">
          <PageHeader sections={sections} />
          <main
            id="main-content"
            ref={mainRef}
            tabIndex={-1}
            className={`flex min-h-0 flex-1 flex-col outline-none ${overflowClass}`}
          >
            <PageTransition>
              <Suspense fallback={<ContentSkeleton />}>
                <Outlet />
              </Suspense>
            </PageTransition>
          </main>
        </div>
      </div>
    </PageChromeProvider>
  );
}
